// src/components/ai/SolutionSuggestions.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Rating,
  Skeleton,
  Alert,
  Avatar,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Lightbulb,
  TrendingUp,
  People,
  Visibility,
  Verified,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import aiService from '../../services/ai';

const SolutionSuggestions = ({ problemId, onError }) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, [problemId]);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const data = await aiService.getSolutionSuggestions(problemId);
      setSuggestions(data.suggestions || []);
    } catch (err) {
      setError('Failed to load AI suggestions');
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          <AutoAwesome sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
          AI is finding the best solutions for you...
        </Typography>
        <LinearProgress sx={{ height: 4, borderRadius: 2, mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        <AutoAwesome sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle', color: '#F59E0B' }} />
        AI-Powered Solution Recommendations
      </Typography>

      <Grid container spacing={2}>
        {suggestions.map((suggestion) => (
          <Grid item xs={12} key={suggestion.id}>
            <Card 
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)',
                  borderColor: '#2E7D32',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#E8F5E9' }}>
                      <Lightbulb sx={{ color: '#2E7D32' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {suggestion.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {suggestion.provider?.name}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={`${suggestion.similarity_score}% match`}
                    size="small"
                    sx={{ bgcolor: '#2E7D32', color: 'white' }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {suggestion.tagline || suggestion.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Rating value={suggestion.rating_avg || 0} readOnly size="small" />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <People sx={{ fontSize: 14, color: '#9CA3AF' }} />
                    <Typography variant="caption">{suggestion.subscriber_count || 0}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Visibility sx={{ fontSize: 14, color: '#9CA3AF' }} />
                    <Typography variant="caption">{suggestion.views_count || 0}</Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  {suggestion.categories?.slice(0, 3).map((cat) => (
                    <Chip key={cat.id} label={cat.name} size="small" variant="outlined" />
                  ))}
                </Stack>

                {suggestion.pricing_plans?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#2E7D32' }}>
                      ${suggestion.pricing_plans[0].price}
                      <Typography component="span" variant="caption" color="text.secondary">
                        /{suggestion.pricing_plans[0].billing_cycle || 'month'}
                      </Typography>
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/solutions/${suggestion.id}`)}
                      sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}
                    >
                      View Details
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SolutionSuggestions;